/**
 * Knex implementation of ISingleRepository for KV-style single_records storage.
 *
 * Each single entity stores its value in the `single_records` table using
 * a { key, value } pair. Scoped entities use composite keys like `entity_key:scope_value`.
 *
 * This is distinct from KnexSingleRepository which operates on entities that
 * have their own dedicated table with direct columns.
 *
 * Pure Knex adapter — no service deps, no hooks, no business logic.
 * SingleService handles JSON parsing, envMapping, defaults, and hooks on top.
 */

import type { Knex } from 'knex'
import type { ISingleRepository } from '../types.js'

const SINGLE_RECORDS_TABLE = 'single_records'

export interface KnexSingleKvRepoOptions {
  /** Entity key — used as the `key` column value (or prefix for scoped). */
  entityKey: string
  /** Field name for scoping (e.g. 'provider', 'tenant'). If undefined: global mode. */
  scopeField?: string
  /** Generates IDs for inserts. */
  idGenerator: () => string
  /** Returns current timestamp string compatible with the DB dialect. */
  timestampFn: () => string
}

/**
 * Row shape in single_records table.
 */
interface KvRow {
  id: string
  key: string
  value: string
  created_at: string
  updated_at: string
}

/**
 * Knex-backed ISingleRepository over the single_records KV table.
 *
 * Global mode (no scopeField):
 *   Uses `key = entityKey`. Returns the JSON-parsed value merged with `{ id }`.
 *   `get()` returns the parsed object or null.
 *   `upsert(data)` serializes and upserts the row.
 *
 * Scoped mode (scopeField defined):
 *   Uses `key = entityKey:scopeValue`.
 *   `get(scope)` returns the parsed object with `id` and `scopeField` injected.
 *   `upsert(data, scope)` serializes (stripping id/scopeField) and upserts.
 */
export class KnexSingleKvRepository<T extends Record<string, unknown> = Record<string, unknown>>
  implements ISingleRepository<T>
{
  constructor(
    private readonly knex: Knex,
    private readonly options: KnexSingleKvRepoOptions
  ) {}

  private get entityKey(): string {
    return this.options.entityKey
  }

  private scopedKey(scope: string): string {
    return `${this.entityKey}:${scope}`
  }

  private parseValue(raw: unknown): Record<string, unknown> {
    if (typeof raw === 'object' && raw !== null) return raw as Record<string, unknown>
    if (typeof raw === 'string') return JSON.parse(raw) as Record<string, unknown>
    throw new Error('Unexpected value type in single_records')
  }

  // ── get ─────────────────────────────────────────────────────────────────

  async get(scope?: string): Promise<T | null> {
    const { scopeField } = this.options

    if (scopeField && scope !== undefined) {
      const row = await this.knex(SINGLE_RECORDS_TABLE)
        .where('key', this.scopedKey(scope))
        .first<KvRow | undefined>()

      if (!row) return null

      const value = this.parseValue(row.value)
      return {
        ...value,
        id: row.id,
        [scopeField]: scope,
      } as unknown as T
    }

    // Global mode — single record with key = entityKey
    const row = await this.knex(SINGLE_RECORDS_TABLE)
      .where('key', this.entityKey)
      .first<KvRow | undefined>()

    if (!row) return null

    const value = this.parseValue(row.value)
    return { ...value, id: row.id } as unknown as T
  }

  // ── upsert ──────────────────────────────────────────────────────────────

  async upsert(data: Partial<T>, scope?: string): Promise<T> {
    const { scopeField, idGenerator, timestampFn } = this.options
    const now = timestampFn()

    if (scopeField && scope !== undefined) {
      const fullKey = this.scopedKey(scope)
      const existing = await this.knex(SINGLE_RECORDS_TABLE)
        .where('key', fullKey)
        .first<KvRow | undefined>()

      // Strip meta fields before serializing
      const serialized = this.stripMetaFields(data, scopeField)

      if (existing) {
        // Merge with current stored value then update
        const currentValue = this.parseValue(existing.value)
        const merged = { ...currentValue, ...serialized }
        await this.knex(SINGLE_RECORDS_TABLE)
          .where('key', fullKey)
          .update({ value: JSON.stringify(merged), updated_at: now })

        return (await this.get(scope)) as T
      } else {
        await this.knex(SINGLE_RECORDS_TABLE).insert({
          id: idGenerator(),
          key: fullKey,
          value: JSON.stringify(serialized),
          created_at: now,
          updated_at: now,
        })
        return (await this.get(scope)) as T
      }
    }

    // Global mode
    const serialized = this.stripMetaFields(data)
    const existing = await this.knex(SINGLE_RECORDS_TABLE)
      .where('key', this.entityKey)
      .first<KvRow | undefined>()

    if (existing) {
      const currentValue = this.parseValue(existing.value)
      const merged = { ...currentValue, ...serialized }
      await this.knex(SINGLE_RECORDS_TABLE)
        .where('key', this.entityKey)
        .update({ value: JSON.stringify(merged), updated_at: now })
    } else {
      await this.knex(SINGLE_RECORDS_TABLE).insert({
        id: idGenerator(),
        key: this.entityKey,
        value: JSON.stringify(serialized),
        created_at: now,
        updated_at: now,
      })
    }

    return (await this.get()) as T
  }

  // ── helpers ──────────────────────────────────────────────────────────────

  /**
   * Strip framework-internal and meta fields that should not be stored
   * in the JSON value (id, scopeField, _* prefixed fields).
   */
  private stripMetaFields(data: Partial<T>, scopeField?: string): Record<string, unknown> {
    const clean: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(data)) {
      if (k === 'id') continue
      if (scopeField && k === scopeField) continue
      if (k.startsWith('_')) continue
      clean[k] = v
    }
    return clean
  }
}
